import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // No more localStorage token - rely entirely on httpOnly cookie
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/session`, { withCredentials: true });
      if (response.data?.user_id) {
        setIsAuthenticated(true);
        try {
          const profileResponse = await axios.get(`${API_URL}/profile/me`, { withCredentials: true });
          setUser(profileResponse.data);
          setOnboardingCompleted(profileResponse.data.onboarding_completed || false);
        } catch {
          // Profile load failed but user is authenticated
        }
      } else {
        setIsAuthenticated(false);
      }
    } catch {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const loadProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/profile/me`, { withCredentials: true });
      setUser(response.data);
      setOnboardingCompleted(response.data.onboarding_completed || false);
      setIsAuthenticated(true);
    } catch (error) {
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password }, { withCredentials: true });
    setIsAuthenticated(true);
    
    try {
      const profileResponse = await axios.get(`${API_URL}/profile/me`, { withCredentials: true });
      setUser(profileResponse.data);
      setOnboardingCompleted(profileResponse.data.onboarding_completed || false);
    } catch {
      // Profile load failed, but login succeeded
    }
    setLoading(false);
    
    return response.data;
  }, []);

  const signup = useCallback(async (email, password, name = null) => {
    const response = await axios.post(`${API_URL}/auth/signup`, { email, password, name }, { withCredentials: true });
    setIsAuthenticated(true);
    
    try {
      const profileResponse = await axios.get(`${API_URL}/profile/me`, { withCredentials: true });
      setUser(profileResponse.data);
      setOnboardingCompleted(profileResponse.data.onboarding_completed || false);
    } catch {
      setOnboardingCompleted(false);
    }
    setLoading(false);
    
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Logout endpoint failed, but proceed anyway
    }
    setIsAuthenticated(false);
    setUser(null);
    setOnboardingCompleted(false);
  }, []);

  const completeOnboarding = useCallback(async () => {
    setOnboardingCompleted(true);
    // Refresh user data but don't let it override onboardingCompleted
    try {
      const response = await axios.get(`${API_URL}/profile/me`, { withCredentials: true });
      setUser(response.data);
      // Don't call setOnboardingCompleted here - we just set it to true above
    } catch {
      // Profile refresh failed, but onboarding is complete
    }
  }, []);

  // For backward compatibility, expose `token` as a boolean-ish value
  // Components check `if (token)` to see if user is logged in
  const token = isAuthenticated;

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ 
    token,
    isAuthenticated,
    user, 
    onboardingCompleted,
    loading,
    login, 
    signup, 
    logout, 
    completeOnboarding,
    loadProfile
  }), [token, isAuthenticated, user, onboardingCompleted, loading, login, signup, logout, completeOnboarding, loadProfile]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
